import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import * as ac from "../../actions/report";

import { initialState } from "./reportInitialState";
import type { ReportState } from "./types";

const reportReducer = createReducer<ReportState, ActionType<typeof ac>>(initialState)
    .handleAction(
        ac.addMetricData,
        (state, { payload }): ReportState => ({
            ...state,
            metrics: { ...state.metrics, ...payload },
        }),
    )
    .handleAction(
        ac.initMetricData,
        (state, { payload }): ReportState => ({
            ...state,
            metrics: payload,
        }),
    )
    .handleAction(
        ac.getMetrics.request,
        (state): ReportState => ({
            ...state,
            isLoading: true,
        }),
    )
    .handleAction(
        ac.getMetrics.success,
        (state): ReportState => ({
            ...state,
            error: null,
            isLoading: false,
            isLoaded: true,
        }),
    )
    .handleAction(
        ac.getMetrics.failure,
        (state, { payload }): ReportState => ({
            ...state,
            isLoading: false,
            error: payload,
        }),
    )
    .handleAction(
        ac.setIsLoadingManually,
        (state, { payload }): ReportState => ({
            ...state,
            isLoading: payload,
        }),
    )
    .handleAction(
        ac.getMetaData.success,
        (state, { payload }): ReportState => ({
            ...state,
            metaData: {
                ...state.metaData,
                digestSubscriptionInfo: payload.digestSubscriptionInfo,
                isLoading: false,
            },
        }),
    )
    .handleAction(
        ac.getMetaData.request,
        (state): ReportState => ({
            ...state,
            metaData: {
                ...state.metaData,
                isLoading: !state.metaData.digestSubscriptionInfo,
            },
        }),
    )
    .handleAction(
        ac.getMetaData.failure,
        (state): ReportState => ({
            ...state,
            metaData: {
                ...state.metaData,
                isLoading: false,
            },
        }),
    )
    .handleAction(
        ac.getCompetitors.request,
        (state): ReportState => ({
            ...state,
            competitorsModal: {
                ...state.competitorsModal,
                isLoading: true,
            },
        }),
    )
    .handleAction(
        ac.getCompetitors.success,
        (state, { payload: { competitors, metricName } }): ReportState => ({
            ...state,
            competitorsModal: {
                ...state.competitorsModal,
                isLoading: false,
                competitors: {
                    ...state.competitorsModal.competitors,
                    [metricName]: competitors,
                },
            },
        }),
    )
    .handleAction(
        ac.getCompetitors.failure,
        (state): ReportState => ({
            ...state,
            competitorsModal: {
                ...state.competitorsModal,
                isLoading: false,
            },
        }),
    )
    .handleAction(
        ac.getMetricItemImage.failure,
        (state): ReportState => ({
            ...state,
            assets: { ...state.assets, isImagesFetched: false },
        }),
    )
    .handleAction(
        ac.getMetricItemImage.success,
        (state, { payload }): ReportState => ({
            ...state,
            assets: { images: payload, isImagesFetched: true },
        }),
    )
    .handleAction(
        ac.resetImages,
        (state): ReportState => ({
            ...state,
            assets: initialState.assets,
        }),
    )
    .handleAction(
        ac.setAudienceReachAutoDuration,
        (state, { payload }): ReportState => ({
            ...state,
            audienceReachAutoDuration: payload,
        }),
    )
    .handleAction(
        ac.downloadPdf.request,
        (state): ReportState => ({
            ...state,
            contextMenu: {
                ...state.contextMenu,
                isLoading: true,
            },
        }),
    )
    .handleAction(
        ac.downloadPdf.success,
        (state): ReportState => ({
            ...state,
            contextMenu: {
                ...state.contextMenu,
                isLoading: false,
            },
        }),
    )
    .handleAction(
        ac.downloadPdf.failure,
        (state, { payload }): ReportState => ({
            ...state,
            contextMenu: {
                pdfState: {
                    ...state.contextMenu.pdfState,
                    error: payload,
                },
                isLoading: false,
            },
        }),
    )
    .handleAction(
        ac.openSubscriptionModal,
        (state): ReportState => ({
            ...state,
            subscriptionModal: {
                ...state.subscriptionModal,
                isOpen: true,
            },
        }),
    )
    .handleAction(
        ac.closeSubscriptionModal,
        (state): ReportState => ({
            ...state,
            subscriptionModal: {
                ...state.subscriptionModal,
                isOpen: false,
            },
        }),
    );

export default reportReducer;
